import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import {
  organizationsApi,
  OrganizationFilters,
  OrganizationWorkspaceFilters,
  InviteUserPayload,
  UpdateSubscriptionPayload,
  CreateOrganizationPayload,
} from '@/api/organizations';

/**
 * Query key factory for organizations
 */
export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: (filters: OrganizationFilters) => [...organizationKeys.lists(), filters] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (id: string) => [...organizationKeys.details(), id] as const,
  workspaces: (id: string, filters?: OrganizationWorkspaceFilters) =>
    [...organizationKeys.detail(id), 'workspaces', filters] as const,
  members: (id: string, params?: any) =>
    [...organizationKeys.detail(id), 'members', params] as const,
};

/**
 * Hook to fetch paginated list of organizations
 */
export function useOrganizations(filters: OrganizationFilters) {
  return useQuery({
    queryKey: organizationKeys.list(filters),
    queryFn: () => organizationsApi.getAll(filters),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single organization by ID
 */
export function useOrganization(id: string) {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: () => organizationsApi.getById(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch workspaces for an organization
 */
export function useOrganizationWorkspaces(id: string, filters?: OrganizationWorkspaceFilters) {
  return useQuery({
    queryKey: organizationKeys.workspaces(id, filters),
    queryFn: () => organizationsApi.getWorkspaces(id, filters),
    enabled: !!id,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch members for an organization
 */
export function useOrganizationMembers(id: string, params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: organizationKeys.members(id, params),
    queryFn: () => organizationsApi.getMembers(id, params),
    enabled: !!id,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
}

/**
 * Hook to create a new organization
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: CreateOrganizationPayload) => organizationsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      toast({
        title: 'Success',
        description: 'Organization created successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to create organization',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update organization subscription status
 */
export function useUpdateSubscription() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSubscriptionPayload }) =>
      organizationsApi.updateSubscription(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: `Organization subscription ${variables.payload.subscribed ? 'activated' : 'deactivated'}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update subscription',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to restore a soft-deleted organization
 */
export function useRestoreOrganization() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => organizationsApi.restore(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      toast({
        title: 'Success',
        description: 'Organization restored successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to restore organization',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to invite a user to an organization
 */
export function useInviteUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: InviteUserPayload }) =>
      organizationsApi.inviteUser(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: 'User invitation sent successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to send invitation',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to resend an invitation to a user
 */
export function useResendInvite() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) =>
      organizationsApi.resendInvite(id, email),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Invitation resent successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to resend invitation',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to remove a member from an organization
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      organizationsApi.removeMember(id, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: 'Member removed successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to remove member',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update a member's role in an organization
 */
export function useUpdateOrganizationMemberRole() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, userId, role }: { id: string; userId: string; role: string }) =>
      organizationsApi.updateMemberRole(id, userId, role),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: 'Member role updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update member role',
        variant: 'destructive',
      });
    },
  });
}
